import { TempUnit } from '@/app/models/weather-widget.model';
import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { UserLocation } from '../../../../models/location.model';
import { DailyForecast, WeeklyWeather } from '../models/weather.model';
import moment from 'moment';

export type ChartTab = 'temperature' | 'precipitation' | 'wind';
@Injectable({ providedIn: 'root' })
export class WeatherStore {
  readonly location = signal<UserLocation | null>(null);
  readonly unit = signal<TempUnit>(TempUnit.F);
  readonly selectedTab = signal<ChartTab>('temperature');
  readonly selectedDayIdx = signal<number>(0);

  // Subject to trigger settings save when location or unit changes
  private readonly userSettingsChangedSource = new Subject<void>();
  readonly userSettingsChanged$ = this.userSettingsChangedSource.asObservable();

  readonly selectedLocationId = computed<string | null>(
    () => {
      const loc = this.location();
      return loc?.key ?? null;
    },
    {
      equal: (a, b) => a === b,
    }
  );

  readonly todayIdx = computed(() => {
    const days = this.data()?.days;
    if (!days?.length) return 0;
   // get locations offset
    const offset = moment.parseZone(days[0].date).utcOffset();
    // create now and apply offset to get local time in location
    const localToday = moment().utcOffset(offset).format('YYYY-MM-DD');
    // find index of the day that matches local today date
    const index = days.findIndex((d) => moment.parseZone(d.date).format('YYYY-MM-DD') === localToday);
    return index !== -1 ? index : 0;
  });

  /** Data States */
  readonly loading = signal<boolean>(true);
  readonly data = signal<WeeklyWeather | null>(null);
  readonly error = signal<string | null>(null);

  readonly currentDay = computed<DailyForecast | null>(() => {
    const d = this.data();
    if (!d) return null;
    return d.days[this.selectedDayIdx()] ?? null;
  });

  readonly headerTemp = computed<number | null>(() => {
    const day = this.currentDay();
    if (!day || !day.hourly || day.hourly.length === 0) return null;
    const idx = new Date().getHours();
    const hp = day.hourly[idx] ?? day.hourly[0];
    return hp?.temp ?? null;
  });

  readonly headerSummary = computed(() => this.currentDay()?.summaryCode ?? null);

  readonly hourlySeries = computed(() => {
    const day = this.currentDay();
    if (!day) return { labels: [] as string[], values: [] as number[] };

    const reordered = [...day.hourly].sort((a, b) => {
      return new Date(a.time).getTime() - new Date(b.time).getTime();
    });

    const tab = this.selectedTab();
    const labels = reordered.map((p) => {
      return moment.parseZone(p.time).format('HH:mm');
    });

    let values: number[];
    if (tab === 'temperature') {
      values = reordered.map((p) => p.temp);
    } else if (tab === 'precipitation') {
      values = reordered.map((p) => p.precipMm);
    } else {
      values = reordered.map((p) => p.windKph);
    }

    return { labels, values };
  });

  setUnit(unit: TempUnit) {
    this.unit.set(unit);
  }

  setLocation(location: UserLocation) {
    this.location.set(location);
  }

  setLoading(loading: boolean) {
    this.loading.set(loading);
  }

  setError(error: string | null) {
    this.error.set(error);
  }

  setData(data: WeeklyWeather | null) {
    this.data.set(data);
    if (data) {
      this.selectedDayIdx.set(this.todayIdx());
    }
  }
  patchConfig(unit: TempUnit, location: UserLocation) {
    this.unit.set(unit);
    this.location.set(location);
  }

  /**
   * Set location and trigger save
   */
  setAndSaveLocation(location: UserLocation) {
    this.location.set(location);
    this.userSettingsChangedSource.next();
  }

  /**
   * Set unit and trigger save
   */
  setAndSaveUnit(unit: TempUnit) {
    this.unit.set(unit);
    this.userSettingsChangedSource.next();
  }
}
